// Unified payer/plan type classification for patient insurance.
// PLAN_TYPE_OPTIONS is the single source of truth — used on patient insurance,
// fee schedule payer category, billing rules, and reporting.

type PlanTypeOption = { value: string; label: string };
type PlanTypeGroup = { group: string; options: PlanTypeOption[] };
type PlanTypeEntry = PlanTypeOption | PlanTypeGroup;

function isGroup(entry: PlanTypeEntry): entry is PlanTypeGroup {
  return 'group' in entry;
}

export const PLAN_TYPE_OPTIONS: readonly PlanTypeEntry[] = [
  { value: 'medicare', label: 'Medicare' },
  { value: 'medicare_advantage', label: 'Medicare Advantage' },
  { value: 'medicaid', label: 'Medicaid' },
  { group: 'Commercial', options: [
    { value: 'commercial_ppo', label: 'PPO' },
    { value: 'commercial_hmo', label: 'HMO' },
    { value: 'commercial_epo', label: 'EPO' },
    { value: 'commercial_pos', label: 'POS' },
    { value: 'commercial_other', label: 'Commercial (Other)' },
  ]},
  { value: 'workers_comp', label: "Workers' Comp" },
  { value: 'auto', label: 'Auto / MVA' },
  { value: 'tricare', label: 'Tricare' },
  { value: 'self_pay', label: 'Self-Pay' },
  { value: 'other', label: 'Other' },
];

function flattenOptions(entries: readonly PlanTypeEntry[]): PlanTypeOption[] {
  const result: PlanTypeOption[] = [];
  for (const entry of entries) {
    if (isGroup(entry)) result.push(...entry.options);
    else result.push(entry);
  }
  return result;
}

const ALL_OPTIONS = flattenOptions(PLAN_TYPE_OPTIONS);

export const PLAN_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ALL_OPTIONS.map(o => [o.value, o.label])
);

export const PLAN_TYPE_VALUES = ALL_OPTIONS.map(o => o.value);

// Flat list for contexts that don't need optgroup (payer category on org payers, billing rules, reporting)
export const PAYER_CATEGORIES = ALL_OPTIONS;

export const BILLING_RULE_PAYER_CATEGORIES = [
  { value: 'all', label: 'All Payers' },
  ...ALL_OPTIONS,
];

export const PAYER_CATEGORY_LABELS: Record<string, string> = {
  all: 'All Payers',
  ...PLAN_TYPE_LABELS,
};

export function isCommercial(planType: string | undefined): boolean {
  return !!planType && planType.startsWith('commercial');
}

// Self-pay primaries have no subscriber ID and no clearinghouse payer by definition.
// Payer names are typed by hand, so match the name loosely: lowercase and strip
// separators so "Self-Pay", "Self Pay", "SELFPAY", and "Patient Self-Pay" all hit.
export function isSelfPayPlan(
  insurance: { plan_type?: string; payer_name?: string } | null | undefined
): boolean {
  if (!insurance) return false;
  if (normalizePlanType(insurance.plan_type) === 'self_pay') return true;
  const payerName = insurance.payer_name;
  if (!payerName) return false;
  return payerName.toLowerCase().replace(/[^a-z0-9]/g, '').includes('selfpay');
}

const LEGACY_MAP: Record<string, string> = {
  'PPO': 'commercial_ppo',
  'HMO': 'commercial_hmo',
  'EPO': 'commercial_epo',
  'POS': 'commercial_pos',
  'Medicare': 'medicare',
  'Medicaid': 'medicaid',
  'Other': 'other',
  'health': 'other',
  'Commercial': 'commercial_other',
  'Workers Comp': 'workers_comp',
  'Self Pay': 'self_pay',
  'Self-Pay': 'self_pay',
  'Auto': 'auto',
};

export function normalizePlanType(value: string | undefined): string {
  if (!value) return '';
  return LEGACY_MAP[value] || value;
}

/**
 * Billing-rule payer categories as note-template "Payer Type" condition values.
 * `commercial` stands for every commercial_* plan type; Medicare and Medicare
 * Advantage are distinct.
 */
export const PAYER_TYPE_CONDITION_OPTIONS: readonly PlanTypeOption[] = [
  { value: 'medicare', label: 'Medicare' },
  { value: 'medicare_advantage', label: 'Medicare Advantage' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'commercial', label: 'Commercial (all plans)' },
  { value: 'workers_comp', label: "Workers' Comp" },
  { value: 'auto', label: 'Auto / MVA' },
  { value: 'tricare', label: 'Tricare' },
  { value: 'self_pay', label: 'Self-Pay' },
  { value: 'other', label: 'Other' },
];

/**
 * Does a payer category (billing rule scope / Payer Type condition) cover an
 * insurance entry's plan type? `commercial` covers every commercial_* plan.
 * Billing rules additionally let `medicare` cover medicare_advantage; template
 * conditions treat them as separate plan types.
 */
export function payerCategoryMatches(
  category: string | undefined,
  planType: string | undefined,
  { medicareIncludesAdvantage = false }: { medicareIncludesAdvantage?: boolean } = {}
): boolean {
  if (!category || category === 'all') return true;
  const pt = normalizePlanType(planType);
  if (!pt) return false;
  if (category === pt) return true;
  if (category === 'commercial' && pt.startsWith('commercial_')) return true;
  if (medicareIncludesAdvantage && category === 'medicare' && pt === 'medicare_advantage') return true;
  return false;
}

export function renderPlanTypeSelect(
  value: string,
  onChange: (value: string) => void,
  className: string,
  placeholder?: string
) {
  // Helper used by multiple components — returns JSX props, not JSX itself.
  // Components render their own <select> using PLAN_TYPE_OPTIONS and isGroup().
  return { value, onChange, className, placeholder };
}

// Visit-limit option lists — shared by EditableInsuranceCard and PatientForm.
// pt_ot_combined marks a limit that covers both disciplines together;
// rehab_outpatient is written by the eligibility check's AC benefit code.
export const VISIT_LIMIT_SPECIALTIES: readonly PlanTypeOption[] = [
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'occupational_therapy', label: 'Occupational Therapy' },
  { value: 'pt_ot_combined', label: 'PT/OT Combined' },
  { value: 'speech_language_pathology', label: 'Speech Language Pathology' },
  { value: 'chiropractic', label: 'Chiropractic' },
  { value: 'rehab_outpatient', label: 'Rehab (Outpatient)' },
  { value: 'general', label: 'General' },
];

export const VISIT_LIMIT_PERIODS: readonly PlanTypeOption[] = [
  { value: 'calendar_year', label: 'Calendar Year' },
  { value: 'plan_year', label: 'Plan Year' },
  { value: 'per_condition', label: 'Per Condition' },
  { value: 'per_episode', label: 'Per Episode' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'custom', label: 'Custom…' },
];

/** Periods that take a period_start date: plan-year start, or an optional renewal anniversary on per-case limits. */
export const VISIT_LIMIT_DATED_PERIODS: readonly string[] = ['plan_year', 'per_condition', 'per_episode'];

/** Human label for a visit limit's period, including its start / renewal date when set. */
export function visitLimitPeriodLabel(limit: { period?: string; period_label?: string; period_start?: string } | null | undefined): string {
  if (!limit) return '';
  if (limit.period === 'custom') return limit.period_label || 'Custom';
  const base = VISIT_LIMIT_PERIODS.find(p => p.value === limit.period)?.label || limit.period || '';
  if (!limit.period_start || !VISIT_LIMIT_DATED_PERIODS.includes(limit.period || '')) return base;
  return limit.period === 'plan_year' ? `${base} from ${limit.period_start}` : `${base}, renews ${limit.period_start.slice(5)}`;
}

export { isGroup };
export type { PlanTypeOption, PlanTypeGroup, PlanTypeEntry };
